import Axios from 'axios'
import createPdfObjUrl from 'src/utils/CreatePdfObjUrl'
import { DocumentType } from 'src/models/enums/DocumentType.enum'
import { IGlobalInvoicePaymentSummary } from 'src/viewModels/InvoicePaymentSummaryResult'
import { pagedList } from 'src/viewModels/pagedList'
import { IQuoteCalculationRequest } from 'src/viewModels/QuoteCalculationRequest'
import { IAnesthesiaLineItemDTO } from 'src/viewModels/quotes/AnesthesiaLineItemDTO'
import { DocumentButtonSummaryResult } from 'src/viewModels/quotes/DocumentButtonSummaryResult'
import { EmailSummaryResult } from 'src/viewModels/quotes/EmailSummaryResult'
import { GlobalInvoiceDTO } from 'src/viewModels/quotes/GlobalInvoiceDTO'
import { InvoiceLogDTO } from 'src/viewModels/quotes/InvoiceLogDTO'
import { InvoiceLogSearchRequest } from 'src/viewModels/quotes/InvoiceLogSearchRequest'
import { IMiscellaneousLineItemDTO } from 'src/viewModels/quotes/MiscellaneousLineItemDTO'
import { IPhysicalTherapyLineItemDTO } from 'src/viewModels/quotes/PhysicalTherapyLineItemDTO'
import { IProfessionalComponentLineItemDTO } from 'src/viewModels/quotes/ProfessionalComponentLineItemDTO'
import { QuoteDTO } from 'src/viewModels/quotes/QuoteDTO'
import { QuoteStatusLogDTO } from 'src/viewModels/quotes/QuoteStatusLogDTO'
import { IQuoteTotalsSummary } from 'src/viewModels/quotes/QuoteTotalsSummary'
import { ITechnicalComponentLineItemDTO } from 'src/viewModels/quotes/TechnicalComponentLineItemDTO'
import { IQuoteSearchRequest } from 'src/viewModels/QuoteSearchRequest'
import { SearchRequestBase } from 'src/viewModels/SearchRequestBase'
import { ButtonLabelSummaryResult } from 'src/viewModels/quotes/ButtonLabelSummaryResult'
import { BaseQuoteLineItemDTO } from 'src/viewModels/quotes/BaseQuoteLineItemDTO'

const base_url = process.env.REACT_APP_BASE_URL || 'http://localhost:5000'

export async function CreateAdjusterEmail(id: string): Promise<EmailSummaryResult> {
  const response = await Axios.get<EmailSummaryResult>(
    `${base_url}/api/quote/${id}/email`
  )

  return response.data
}

export async function searchQuotes(req: IQuoteSearchRequest): Promise<pagedList> {
  //Send search request
  const response = await Axios.post<pagedList>(`${base_url}/api/quote/search`, req)

  return response.data
}

export async function GetQuoteById(id: string): Promise<QuoteDTO> {
  const response = await Axios.get<QuoteDTO>(`${base_url}/api/quote/${id}`)

  return response.data
}

export async function GetQuoteByIdWithLineItems(id: string): Promise<QuoteDTO> {
  const response = await Axios.get<QuoteDTO>(
    `${base_url}/api/quote/${id}/with-line-items`
  )

  return response.data
}

export async function calculateLineItem(
  req: IQuoteCalculationRequest
): Promise<BaseQuoteLineItemDTO> {
  const response = await Axios.post<BaseQuoteLineItemDTO>(
    `${base_url}/api/quote/total`,
    req
  )

  return response.data
}

export async function UpdateStatusLog(req: QuoteStatusLogDTO): Promise<string> {
  const response = await Axios.patch<string>(
    `${base_url}/api/quote/${req.quoteId}/status`,
    req
  )

  return response.data
}

export async function AddInvoiceLog(
  quoteId: string,
  req: InvoiceLogDTO
): Promise<string> {
  const response = await Axios.post<string>(
    `${base_url}/api/quote/${quoteId}/invoice/${req.invoiceId}/log`,
    req
  )

  return response.data
}

export async function GenerateInvoicePDF(
  quoteId: string,
  templateType: DocumentType
): Promise<string> {
  const response = await Axios.get<string>(
    `${base_url}/api/quote/${quoteId}/generate-invoice-pdf`,
    { params: { type: templateType } }
  )

  window.open(createPdfObjUrl(response.data), '_blank')
  return response.data
}

export async function PreviewInvoicePDF(
  quoteId: string,
  req: GlobalInvoiceDTO
): Promise<string> {
  const response = await Axios.post<string>(
    `${base_url}/api/quote/${quoteId}/preview-invoice-pdf`,
    req
  )

  window.open(createPdfObjUrl(response.data), '_blank')
  return response.data
}

export async function GetInvoiceLogByInvoiceId(
  quoteId: string,
  req?: InvoiceLogSearchRequest
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/quote/${quoteId}/invoice-log`,
    req
  )
  return response.data
}

export async function GetInvoicePaymentDetailsByQuoteId(
  quoteId: string
): Promise<IGlobalInvoicePaymentSummary> {
  const response = await Axios.get<IGlobalInvoicePaymentSummary>(
    `${base_url}/api/quote/${quoteId}/invoice-payment-details`
  )
  return response.data
}

export async function generateCostSavings(
  req: IQuoteSearchRequest,
  bundled: boolean
): Promise<string> {
  const response = await Axios.post<string>(
    `${base_url}/api/quote/cost-savings-pdf`,
    req,
    {
      params: {
        bundled,
      },
    }
  )

  window.open(createPdfObjUrl(response.data), '_blank')
  return response.data
}

export async function GetQuoteLogById(
  quoteId: string,
  req?: SearchRequestBase
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/quote/${quoteId}/status-log`,
    req
  )
  return response.data
}

export async function AddStatusLogComment(id: string, req: string): Promise<string> {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: 'application/json; charset=utf-8',
  }
  const response = await Axios.post<string>(
    `${base_url}/api/quote/${id}/add-comment-log`,
    req,
    { headers }
  )

  return response.data
}

export async function CopyQuote(id: string): Promise<QuoteDTO> {
  const response = await Axios.get<QuoteDTO>(`${base_url}/api/quote/${id}/copy`)
  return response.data
}

export interface QuotePotentialMatch {
  id: string
  quoteMatch: string
  matchStatus: string
  matchSurgeryProviderGroup: string
  matchFacilityProviderGroup: string
  matchingCPTs: string
}

export async function GetQuotePotentialMatches(
  quoteId: string
): Promise<QuotePotentialMatch[]> {
  const response = await Axios.get<QuotePotentialMatch[]>(
    `${base_url}/api/quote/${quoteId}/potential-matches`
  )
  return response.data
}

export interface CopySourceLineItemSummary {
  id: string
  type: 'PC' | 'TC' | 'AN' | 'PT' | 'MI'
  providerId?: string
  providerName?: string
  physicianName?: string
  cptCode?: string
  typeText?: string
  lineDescription?: string
  cost?: number
  actualCost?: number
  bilateral?: boolean
  percentageOfContract?: number
  multipleProcedurePercentage?: number
  assistantMultiplier?: number
  monitoredMultiplier?: number
  otherDescription?: string
  numberOfVisits?: number
  costPerVisit?: number
  actualCostPerVisit?: number
  costSource?: number
  reconciledCost?: number
  effectiveCost?: number
}

export interface CopySourceLineItemsResponse {
  sourceQuoteId: string
  sourceQuoteLabel: string
  lineItems: CopySourceLineItemSummary[]
}

export async function GetCopySourceLineItems(
  sourceQuoteId: string
): Promise<CopySourceLineItemsResponse> {
  const response = await Axios.get<CopySourceLineItemsResponse>(
    `${base_url}/api/quote/${sourceQuoteId}/copy-source-line-items`
  )
  return response.data
}

export interface CopyQuoteFromSourceRequest {
  sourceQuoteId: string
  lineItemIds: ConsumerQuoteLineItemRef[]
  assignedClaimIds: string[]
}

export async function GetQuoteTotalsSummary(
  req: IQuoteSearchRequest
): Promise<IQuoteTotalsSummary> {
  const response = await Axios.post<IQuoteTotalsSummary>(
    `${base_url}/api/quote/totals`,
    req
  )
  return response.data
}

export async function GetAnesthesiaLineItems(
  quoteId: string
): Promise<IAnesthesiaLineItemDTO[]> {
  const response = await Axios.get<IAnesthesiaLineItemDTO[]>(
    `${base_url}/api/quote/${quoteId}/anesthesia-line-items`
  )

  return response.data
}

export async function GetMiscellaneousLineItems(
  quoteId: string
): Promise<IMiscellaneousLineItemDTO[]> {
  const response = await Axios.get<IMiscellaneousLineItemDTO[]>(
    `${base_url}/api/quote/${quoteId}/miscellaneous-line-items`
  )

  return response.data
}

export async function GetPhysicalTherapyLineItems(
  quoteId: string
): Promise<IPhysicalTherapyLineItemDTO[]> {
  const response = await Axios.get<IPhysicalTherapyLineItemDTO[]>(
    `${base_url}/api/quote/${quoteId}/physical-therapy-line-items`
  )

  return response.data
}

export async function GetProfessionalComponentLineItems(
  quoteId: string
): Promise<IProfessionalComponentLineItemDTO[]> {
  const response = await Axios.get<IProfessionalComponentLineItemDTO[]>(
    `${base_url}/api/quote/${quoteId}/professional-component-line-items`
  )

  return response.data
}

export async function GetTechnicalComponentLineItems(
  quoteId: string
): Promise<ITechnicalComponentLineItemDTO[]> {
  const response = await Axios.get<ITechnicalComponentLineItemDTO[]>(
    `${base_url}/api/quote/${quoteId}/technical-component-line-items`
  )

  return response.data
}

export async function GetClaimButtonsWithSummary(
  quoteId: string,
  providerType: string
): Promise<{
  claimButtons: DocumentButtonSummaryResult[]
  providerTypeSummaries: ButtonLabelSummaryResult[]
}> {
  const response = await Axios.get<{
    claimButtons: DocumentButtonSummaryResult[]
    providerTypeSummaries: ButtonLabelSummaryResult[]
  }>(`${base_url}/api/quote/${quoteId}/claim-buttons-with-summary`, {
    params: { providerType },
  })

  return response.data
}

export interface ConsumerQuoteLineItemRef {
  id: string
  type: string
}

export interface ConsumerQuoteSaveRequest {
  consumedQuoteId: string
  lineItemIds: ConsumerQuoteLineItemRef[]
  assignedClaimIds: string[]
}

export async function SaveConsumerQuote(
  consumedQuoteId: string,
  request: ConsumerQuoteSaveRequest
): Promise<void> {
  await Axios.post(`${base_url}/api/quote/${consumedQuoteId}/consumer`, request)
}
